import { useEffect, useId, useRef, useState } from 'react';

const LEN = 6;

type Props = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Povolí přepnutí na záložní kód (XXXXX-XXXXX) odkazem pod políčky. */
  recovery?: boolean;
  /** Červený rámeček a zatřesení – po špatném kódu, dokud se nezačne psát znovu. */
  invalid?: boolean;
  autoFocus?: boolean;
  id?: string;
};

/**
 * Kód z autentikátoru do šesti políček (jako Telegram). Skutečné je jen JEDNO pole položené
 * přes šest kreslených políček: vložení celého kódu, automatické vyplnění ze správce hesel
 * i čtečka obrazovky fungují jako u obyčejného inputu. Po šesté číslici se formulář odešle sám.
 */
export function OtpField({ label, value, onChange, recovery, invalid, autoFocus, id }: Props) {
  const auto = useId();
  const inputId = id ?? auto;
  const ref = useRef<HTMLInputElement>(null);
  const done = useRef('');
  const [text, setText] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  const toEnd = () => {
    const el = ref.current;
    if (!el || text) return;
    el.setSelectionRange(el.value.length, el.value.length);
  };

  const handle = (raw: string) => {
    const next = text ? raw.slice(0, 11) : raw.replace(/\D/g, '').slice(0, LEN);
    onChange(next);
    if (text || next.length < LEN) {
      done.current = '';
      return;
    }
    if (next === done.current) return;
    done.current = next;
    // odeslat až po překreslení, ať formulář čte už novou hodnotu
    setTimeout(() => ref.current?.form?.requestSubmit?.(), 0);
  };

  const at = Math.min(value.length, LEN - 1);
  const slots = Array.from({ length: LEN }, (_, i) => i);
  return (
    <div className="field">
      <label htmlFor={inputId}>{label}</label>
      <div className={`otp ${text ? 'is-text' : ''} ${invalid ? 'is-error' : ''}`.trim()}>
        <div className="otp-row">
          {!text &&
            slots.map((i) => (
              <div
                key={i}
                aria-hidden="true"
                className={`otp-slot ${i < value.length ? 'is-filled' : ''} ${focused && i === at ? 'is-active' : ''}`.trim()}
              >
                {value.charAt(i)}
              </div>
            ))}
          <input
            id={inputId}
            ref={ref}
            className={text ? 'otp-text' : 'otp-input'}
            value={value}
            onChange={(e) => handle(e.target.value)}
            onFocus={() => {
              setFocused(true);
              setTimeout(toEnd, 0);
            }}
            onBlur={() => setFocused(false)}
            onClick={toEnd}
            inputMode={text ? 'text' : 'numeric'}
            autoComplete={text ? 'off' : 'one-time-code'}
            autoCapitalize={text ? 'characters' : undefined}
            placeholder={text ? 'XXXXX-XXXXX' : undefined}
            maxLength={text ? 11 : LEN}
            required
          />
        </div>
        {recovery && (
          <button
            type="button"
            className="otp-switch"
            onClick={() => {
              setText(!text);
              onChange('');
              done.current = '';
              ref.current?.focus();
            }}
          >
            {text ? 'Zpět na kód z aplikace' : 'Nemám telefon, zadám záložní kód'}
          </button>
        )}
      </div>
    </div>
  );
}
